import React, { useState } from 'react';
import MainLayout from '../components/layout/MainLayout';
import ConsultationNote from '../components/documentation/ConsultationNote';
import DocumentActions from '../components/documentation/DocumentActions';
import { toast } from 'react-toastify';

const Documentation = () => {
  const [noteState, setNoteState] = useState({
    status: 'Draft',
    isDirty: false,
    aiEnabled: true,
    lastSaved: null
  });

  const [patientData] = useState({
    name: "Sarah Smith",
    age: 42,
    gender: "F",
    date: "November 15, 2023",
    visitType: "New Consultation"
  });

  const [noteContent, setNoteContent] = useState({
    chiefComplaint: "",
    historyOfPresentIllness: "42-year-old female presenting with right breast mass noted on self-exam 3 weeks ago. No associated pain, skin changes, or nipple discharge. No prior breast issues.",
    medicalHistory: "No prior cancer. Mother with breast cancer at age 51. No radiation exposure. G2P2. Last mammogram 2 years ago.",
    diagnosticResults: "Mammogram 10/28/23: BIRADS 4C\nUltrasound 11/02/23: 2.1cm mass\nCore biopsy 11/05/23: Invasive ductal carcinoma, grade 2, ER+/PR+, HER2-",
    assessment: "Newly diagnosed early stage breast cancer. Will require"
  });

  // Handlers for document actions
  const handleSaveDraft = () => {
    setNoteState(prev => ({
      ...prev,
      status: 'Draft',
      isDirty: false,
      lastSaved: new Date().toLocaleTimeString()
    }));
    toast.success("Note saved as draft");
  };

  const handleSignComplete = () => {
    setNoteState(prev => ({
      ...prev,
      status: 'Completed',
      isDirty: false,
      lastSaved: new Date().toLocaleTimeString()
    }));
    toast.success("Note signed and completed");
  };

  const handleVoiceDictation = () => {
    toast.info("Voice dictation activated. Speak now...");
    // In a real app, this would activate the speech recognition API
    setTimeout(() => {
      setNoteContent(prev => ({
        ...prev,
        assessment: prev.assessment + " surgical planning and systemic therapy evaluation. Will discuss options at next visit.",
      }));
      toast.success("Voice dictation added to note");
    }, 2000);
  };

  const toggleAISuggestions = () => {
    setNoteState(prev => ({
      ...prev,
      aiEnabled: !prev.aiEnabled
    }));
    toast.info(`AI suggestions ${noteState.aiEnabled ? 'disabled' : 'enabled'}`);
  };

  // Handle consultation note changes
  const handleNoteChange = (field, value) => {
    setNoteContent(prev => ({
      ...prev,
      [field]: value
    }));
    
    setNoteState(prev => ({
      ...prev,
      isDirty: true
    }));
  };

  // Handle template selection
  const handleTemplateSelect = (template) => {
    // In a real app, this would load predefined templates
    const templates = {
      "New Consultation": {
        chiefComplaint: "",
        historyOfPresentIllness: "__ year-old presenting with __.",
        medicalHistory: "Prior medical history:",
        diagnosticResults: "Recent diagnostic findings:",
        assessment: "Assessment and plan:"
      },
      "Follow-up Visit": {
        chiefComplaint: "Follow-up visit",
        historyOfPresentIllness: "Patient returns for scheduled follow-up.",
        medicalHistory: "As previously documented with no significant changes.",
        diagnosticResults: "Recent studies:",
        assessment: "Follow-up assessment:"
      },
      "Treatment Review": {
        chiefComplaint: "Treatment review",
        historyOfPresentIllness: "Patient undergoing treatment for __.",
        medicalHistory: "As previously documented.",
        diagnosticResults: "Response assessment:",
        assessment: "Treatment tolerance and plan:"
      }
    };

    if (templates[template]) {
      // If there's unsaved content, ask for confirmation
      if (noteState.isDirty) {
        if (window.confirm("You have unsaved changes. Load template anyway?")) {
          setNoteContent(templates[template]);
          toast.info(`${template} template loaded`);
        }
      } else {
        setNoteContent(templates[template]);
        toast.info(`${template} template loaded`);
      }
    }
  };

  return (
    <MainLayout
      activeNavItem="documentation"
      searchPlaceholder="Patient: Sarah Smith"
      notifications={3}
    >
      <div className="flex h-full">
        <div className="flex-1 p-6 overflow-auto">
          <ConsultationNote 
            patient={patientData}
            noteContent={noteContent}
            onNoteChange={handleNoteChange}
            onTemplateSelect={handleTemplateSelect}
          />
        </div>
        <div className="w-80">
          <DocumentActions 
            status={noteState.status}
            aiEnabled={noteState.aiEnabled}
            onSaveDraft={handleSaveDraft}
            onSignComplete={handleSignComplete}
            onVoiceDictation={handleVoiceDictation}
            onToggleAI={toggleAISuggestions}
            noteContent={noteContent}
          />
        </div>
      </div>
      <div className="bg-gray-200 text-gray-600 text-sm p-2 border-t border-gray-300">
        Status: {noteState.status} | Autosave {noteState.isDirty ? "pending" : "enabled"} | Template: {patientData.visitType}
        {noteState.lastSaved && ` | Last saved: ${noteState.lastSaved}`}
      </div>
    </MainLayout>
  );
};

export default Documentation;